import { Component, OnInit, signal, input } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProductStoreRefiner } from '@common/refiners/product.refiner';
import { ProductStoreSelector } from '@common/selectors/product.selector';
import {
  ICartDraftDeclinableProductItem,
  ICartDraftSimpleProductItem,
  ICartItem,
} from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { IAdditionGroup } from '@core/models/shop.model';
import { CartService } from '@core/services/cart/cart.service';
import { ProductService } from '@core/services/product/product.service';
import { ProductsService } from '@core/services/products/products.service';
import {
  BehaviorSubject,
  debounceTime,
  delay,
  filter,
  map,
  share,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Location } from '@angular/common';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Step } from '@shared/local-store/stepper/stepper.type';

@Component({
  selector: 'app-kiosk-product-add-page',
  template: `
    @if (product(); as product) {
      <div class="relative">
        @if (
          isProductStepperStable() === 'unstable' || isProductStepperStable() === 'earlyAddToCart'
        ) {
          <div class="absolute inset-0 z-0">
            <div class="flex h-[75vh] flex-col items-center justify-center gap-24">
              @if (isProductStepperStable() === 'earlyAddToCart') {
                <p class="text-4xl">
                  {{ 'kiosk-product-add-page.auto-added-to-cart-product' | translate }}
                </p>
                <p class="text-8xl font-bold">{{ product.name }}</p>
                @if (product.images?.thumbnail?.url) {
                  <img
                    [src]="product.images?.thumbnail?.url"
                    [alt]="product.name"
                    class="max-w-full"
                  />
                } @else {
                  <!-- icon -->
                  <i class="icon icon-add-to-cart-2 animate-bounce text-8xl"></i>
                }
              }
            </div>
          </div>
        }

        <div
          class="relative block h-full overflow-hidden"
          [class.invisible]="isProductStepperStable() !== 'stable'"
        >
          <div
            class="ml-5 flex h-full flex-col gap-5"
            [style.color]="textColor() | contentBuilderColor"
          >
            <app-kiosk-product-details-block
              #productDetailsBlock
              [cardColor]="cardColor()"
              [primaryButtonBackgroundColor]="buttonBackgroundColor()"
              [primaryButtonTextColor]="buttonTextColor()"
              [unavailableButtonBackground]="unavailableButtonBackground()"
              [unavailableButtonText]="unavailableButtonText()"
              [compositionBackgroundColor]="compositionBackgroundColor()"
              [textColor]="textColor()"
              [backgroundColor]="backgroundColor()"
              [secondaryColor]="secondaryColor()"
              (addToCart)="addToCart$.next($event)"
              (selectedStep)="selectedStep$.next($event)"
            >
            </app-kiosk-product-details-block>
          </div>
        </div>
      </div>
    }
  `,
})
export class KioskProductAddPageComponent implements OnInit {
  readonly backgroundColor = input<IContentBuilderFieldColor>();
  readonly secondaryColor = input<IContentBuilderFieldColor>();
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();
  readonly compositionBackgroundColor = input<IContentBuilderFieldColor>();

  private _destroyerRef = new Subject<boolean>();

  protected readonly addToCart$ = new BehaviorSubject<
    | {
        cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
        validity: {
          isValid: boolean;
        };
      }
    | undefined
  >(undefined);

  protected readonly selectedStep$ = new Subject<Step | undefined>();

  private addToCartRequested$ = this.addToCart$.pipe(
    filter(data => !!data),
    map(data => data as NonNullable<typeof data>),
  );

  private readonly isProductStepperStable$ = this.selectedStep$.pipe(
    debounceTime(50),
    take(1),
    switchMap(() => this.addToCart$),
    map(addToCartTriggerEarly => {
      if (addToCartTriggerEarly) {
        return 'earlyAddToCart' as const;
      }
      return 'stable' as const;
    }),
    take(1),
    startWith('unstable' as const),
    share(),
  );

  protected readonly isProductStepperStable = toSignal(this.isProductStepperStable$);

  productId: string | null = null;
  shopId: string | null = null;

  protected readonly product = signal<DoodProductModel | null>(null);
  additionsByGroup$ = this.productRefiner.selectAdditionsByGroups.pipe(
    takeUntil(this._destroyerRef),
  );
  additions$ = this.productRefiner.selectAdditions.pipe(
    map(additions => additions.filter(v => !v.addition_group && v.id)),
  );

  protected readonly quantity = signal(1);

  reconstructedCart$ = this.cartService.getCartLineItems$();

  selectedAdditionsByGroup: (IAdditionGroup & {
    selectedAdditions: string[];
  })[] = [];
  selectedAdditions: string[] = [];

  private readonly activeCategory$ = this.productSelector.selectActiveCategory;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cartService: CartService,
    private readonly productService: ProductService,
    private readonly productsService: ProductsService,
    private readonly productRefiner: ProductStoreRefiner,
    private readonly productSelector: ProductStoreSelector,
    private readonly location: Location,
    private readonly analyticsService: AnalyticsService,
  ) {
    this.isProductStepperStable$
      .pipe(
        filter(isProductStepperStable => isProductStepperStable !== 'unstable'),
        take(1),
        switchMap(isProductStepperStable => {
          if (isProductStepperStable === 'earlyAddToCart') {
            return this.addToCartRequested$.pipe(delay(1300)); // will display the add to cart message during the delay
          }

          return this.addToCartRequested$;
        }),
        takeUntilDestroyed(),
      )
      .subscribe(addToCartData => this.addToCart(addToCartData));
  }

  ngOnInit(): void {
    this.additions$.subscribe();

    this.shopId = this.cartService.getShopId() ?? null;

    this.updateProductId();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => this.updateProductId()),
      )
      .subscribe();
    this.productsService
      .selectByShopAndProduct$(this.shopId, this.productId)
      .pipe(
        take(1),
        map(
          (product: DoodProductModel | undefined) =>
            product === undefined ? null : { ...product, quantity: 0 }, // ensure that the quantity is set to 0 and it avoid to share the same reference, because the product quantity is mutated
        ),

        tap(product => product && this.productService.setProductToStore(product)),
        tap(product => {
          this.product.set(product);
        }),
        takeUntil(this._destroyerRef),
      )
      .subscribe();
    this.route.params.pipe(take(1)).subscribe(() => this.updateProductId());
    this.productRefiner.selectAdditionsByGroups
      .pipe(
        tap(additionsGroups => {
          additionsGroups.forEach(additionGroup => {
            if (
              !this.selectedAdditionsByGroup.find(
                additionGroup => additionGroup.id === additionGroup.id,
              )
            ) {
              this.selectedAdditionsByGroup.push({ ...additionGroup, selectedAdditions: [] });
            }
          });
        }),
      )
      .subscribe();

    this.reconstructedCart$.subscribe();
  }

  private updateProductId(): void {
    const productId = this.getProductIdFromRoute(this.route);
    if (productId) {
      this.productId = productId;
    } else {
      this.productId = null;
    }
  }

  private getProductIdFromRoute(route: ActivatedRoute): string | null {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('productId');
  }

  back(): void {
    const shopCity = this.route.snapshot.paramMap.get('shopCity');
    const shopType = this.route.snapshot.paramMap.get('shopType');
    const shopSlug = this.route.snapshot.paramMap.get('shopSlug');

    this.activeCategory$
      .pipe(
        map(id => {
          this.analyticsService.log({
            level: 'debug',
            label: '#DOOD-1250 Navigate',
            message: {
              from: 'KioskProductAddPageComponent Ligne°177',
              target: [`/fr/${shopCity}/${shopType}/${shopSlug}${id ? `/categories/${id}` : ''}`],
              currentLocation: window.location.pathname,
            },
          });
          this.router.navigate([
            `/fr/${shopCity}/${shopType}/${shopSlug}${id ? `/categories/${id}` : ''}`,
          ]);
        }),
      )
      .subscribe();
  }

  navigateHome() {
    const shopCity = this.route.snapshot.paramMap.get('shopCity');
    const shopType = this.route.snapshot.paramMap.get('shopType');
    const shopSlug = this.route.snapshot.paramMap.get('shopSlug');
    this.analyticsService.log({
      level: 'debug',
      label: '#DOOD-1250 Navigate',
      message: {
        from: 'KioskProductAddPageComponent Ligne°201',
        target: [`/fr/${shopCity}/${shopType}/${shopSlug}/product/${this.productId}/product-add`],
        currentLocation: window.location.pathname,
      },
    });

    this.router.navigate([
      `/fr/${shopCity}/${shopType}/${shopSlug}/product/${this.productId}/product-add`,
    ]);
  }

  addToCart({
    cartDraft,
    validity,
  }: {
    cartDraft: ICartDraftSimpleProductItem | ICartDraftDeclinableProductItem;
    validity: {
      isValid: boolean;
    };
  }): void {
    if (!validity.isValid) {
      return;
    }
    const newCartItem = cartDraft as unknown as Omit<ICartItem, 'id'>; // Once the type of the ICartItem is fixed, this cast will be removed
    if (!newCartItem) return;

    this.cartService
      .addOrUpdateCartItem({ newCartItem, quantity: this.quantity(), product: this.product() })
      .subscribe(() => {
        this.navigateToPreviousPage();
      });
  }

  private navigateToPreviousPage() {
    this.location.back();
  }
}
